/**
 * When each CHAPTER of one subject lands, for each pass — the lower half of a
 * plan's detail page.
 *
 * Deliberately a SECTION under the plan's metadata rather than mixed into it. The
 * two answer different questions — *what is this plan* versus *how does it get
 * there* — and the first cut interleaved them, so a thirty-row grid pushed the
 * name, passes and subjects out of sight.
 *
 * **One subject at a time**, with a selector. Chapters only exist per subject —
 * physics chapter 1 is not maths chapter 1 — so a combined grid would either
 * interleave two syllabuses or need a column group per subject, and both read as
 * a spreadsheet rather than a plan.
 *
 * The dates here are finer than the pass dates on the plan page, and subordinate
 * to them: *"first read done by 30 October"* is the commitment, and these say how
 * it gets there. Editing one cell at a time keeps each history entry exactly what
 * changed.
 *
 * See docs/FeatureDesigns/goals-syllabus-revision-plan/StudyPlanning/shared-plans.md §3.3.
 */
import { useMemo, useState } from 'react';
import { DatePicker } from '../components/DatePicker';
import { Modal } from '../components/Modal';
import { IntegerInput } from '../components/IntegerInput';
import { ResponsiveTable, type ResponsiveColumn } from '../responsive';
import type { StudyPlan, StudyPlanApi, StudyPlanPass } from './types';
import { formatPlanDate, NO_DATE } from './planDates';
import './class-shared.css';

type Chapter = { id: string; number?: string; name: string };

const DAY_MS = 86_400_000;

/** Four weeks. Long enough that a second look at a chapter is a revision rather
 *  than a re-read, and the number a class would say out loud. */
const DEFAULT_GAP_WEEKS = 4;
const isoOf = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * The Friday of the week `iso` falls in.
 *
 * Chapters land on a school week, not on a date — "we finish Banking in the
 * week of the 12th" is the real statement, and Friday is where a week's work
 * closes. Snapping also collapses the near-identical dates an even split
 * produces when there are more chapters than weeks.
 *
 * Weeks run Monday–Sunday, so Sunday belongs to the Friday just gone.
 */
function fridayOf(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  const dow = d.getDay();                       // 0 Sun … 6 Sat
  const shift = dow === 0 ? -2 : 5 - dow;       // Sunday → back to Friday
  return isoOf(new Date(d.getTime() + shift * DAY_MS));
}

/**
 * One date per chapter, evenly spaced from a start up to the pass's own deadline,
 * each snapped to its week's Friday.
 *
 * The last chapter lands ON the deadline's week, not past it: the pass date is
 * the commitment, and a schedule that finishes after it is not a plan.
 */
function evenlySpacedFridays(count: number, endIso: string, startIso: string): string[] {
  const start = Date.parse(`${startIso}T00:00:00`);
  const end = Date.parse(`${endIso}T00:00:00`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || count <= 0) return [];
  // A deadline already past leaves no room to space anything — everything is
  // due in the deadline's week.
  const span = Math.max(0, end - start);
  return Array.from({ length: count }, (_, i) => {
    const at = count === 1 ? end : start + (span * (i + 1)) / count;
    return fridayOf(isoOf(new Date(at)));
  });
}

/** The coming Friday — the default start for the first pass, because a schedule
 *  that begins today asks for work already half done. */
function nextFriday(): string {
  const today = new Date();
  const shift = (5 - today.getDay() + 7) % 7 || 7;
  return isoOf(new Date(today.getTime() + shift * DAY_MS));
}

function addDays(iso: string, days: number): string {
  return isoOf(new Date(Date.parse(`${iso}T00:00:00`) + days * DAY_MS));
}

/**
 * Space the chapters, then push any that would crowd the SAME chapter's previous
 * pass.
 *
 * Re-reading a chapter three days after first reading it is not a revision, so a
 * minimum gap per chapter is a real constraint and not a nicety. It is applied
 * per chapter rather than to the pass as a whole: chapter 1 may have been read in
 * July and chapter 30 in October, so one pass-wide offset would be wrong at both
 * ends.
 *
 * A pushed date may land past the pass's deadline. It is NOT clamped back —
 * silently stacking chapters onto the deadline would hide that the plan does not
 * fit, which is exactly what the caller needs to see.
 */
function spacedWithGap(
  count: number, endIso: string, startIso: string,
  previous: Array<string | undefined>, minGapDays: number,
): { dates: string[]; pushedPastDeadline: number } {
  const base = evenlySpacedFridays(count, endIso, startIso);
  let pushedPastDeadline = 0;
  const dates = base.map((iso, i) => {
    const prev = previous[i];
    if (!prev) return iso;
    const earliest = fridayOf(addDays(prev, minGapDays));
    const chosen = earliest > iso ? earliest : iso;
    if (chosen > endIso) pushedPastDeadline++;
    return chosen;
  });
  return { dates, pushedPastDeadline };
}

export function PlanChapterSchedule({
  api, plan, subject, onSubjectChange, subjectName, chapters = [],
  canEdit = true, editing = false, onPlanChange,
}: {
  api: StudyPlanApi;
  plan: StudyPlan;
  /** Which subject's chapters. The plan may cover several. */
  subject: string;
  onSubjectChange?: (subject: string) => void;
  /** A subject's internal name is a key, never a label. */
  subjectName?: (subject: string) => string;
  chapters?: Chapter[];
  canEdit?: boolean;
  /** The page's one edit mode, owned by `PlansPanel` — the dates here are only
   *  writable when the fields above them are. */
  editing?: boolean;
  onPlanChange?: (plan: StudyPlan) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Said out loud when generation could not honour the gap inside the deadline. */
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * Which pass the generate dialog is open for, and what it will use.
   *
   * NOT persisted: these are inputs to one generation, not properties of the
   * plan. Storing them would imply the plan is regenerated from them later, and
   * it is not — the dates it produced are the plan.
   */
  const [gen, setGen] = useState<{ pass: StudyPlanPass; start: string; gapWeeks: number } | null>(null);

  const entry = plan.subjects?.find(s => s.subject === subject);
  const passes = [...(plan.passes ?? [])].sort((a, b) => a.order - b.order);

  const targets = (passId: string) =>
    new Map((entry?.chapterTargets?.[passId] ?? []).map(t => [t.chapterId, t.targetDate]));

  /**
   * Every chapter of one pass, dated in one go.
   *
   * The one place these editors write more than a cell at a time. The rule
   * against bulk writes is about a HISTORY entry nobody can read; here the whole
   * point is that a route to a deadline is set in a single act, and thirty
   * entries saying "one chapter moved" would describe it worse.
   *
   * Fills THIS subject only — the pass deadline it aims at is shared by every
   * subject in the plan, but the chapters it spaces are this subject's alone.
   */
  const fillChapterDates = async (p: StudyPlanPass, startIso: string, minGapDays: number) => {
    const deadline = plan.passDates?.[p.id];
    if (!deadline || chapters.length === 0) return;
    const key = `fill:${p.id}`;
    setBusy(key); setError(null);
    try {
      // The same chapter's date in the pass BEFORE this one — what the minimum
      // gap is measured from.
      const order = [...(plan.passes ?? [])].sort((a, b) => a.order - b.order);
      const prevPass = order[order.findIndex(x => x.id === p.id) - 1];
      const prevMap = prevPass ? targets(prevPass.id) : new Map<string, string>();
      const previous = chapters.map(ch => prevMap.get(ch.id));

      const { dates, pushedPastDeadline } = spacedWithGap(
        chapters.length, deadline, startIso, previous, minGapDays,
      );
      if (pushedPastDeadline > 0) {
        // Reported, not hidden: the plan does not fit, and clamping the dates
        // back would have concealed exactly that.
        setNotice(`${pushedPastDeadline} chapter${pushedPastDeadline === 1 ? '' : 's'} `
          + `had to go past ${p.name}'s date to keep the ${minGapDays / 7}-week gap.`);
      } else {
        setNotice(null);
      }
      let latest = plan;
      for (const [i, ch] of chapters.entries()) {
        const res = await api.editPlan(plan.id, {
          ownerId: plan.ownerId, passId: p.id, subject, chapterId: ch.id, date: dates[i],
        });
        latest = res.plan;
      }
      onPlanChange?.(latest);
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(null); }
  };

  /**
   * Where this pass starts by default.
   *
   * The first pass starts the coming Friday. A later one starts a gap after the
   * pass before it, because that is the earliest its first chapter could honour
   * the minimum gap — starting them all today would propose a schedule that the
   * gap rule then immediately contradicts.
   */
  const defaultStartFor = (p: StudyPlanPass): string => {
    const order = [...(plan.passes ?? [])].sort((a, b) => a.order - b.order);
    const at = order.findIndex(x => x.id === p.id);
    return at <= 0
      ? nextFriday()
      : fridayOf(addDays(defaultStartFor(order[at - 1]), DEFAULT_GAP_WEEKS * 7));
  };

  const write = async (key: string, passId: string, chapterId: string, date: string | null) => {
    setBusy(key); setError(null);
    try {
      // `subject` is what tells the server WHICH subject's chapter moved — without
      // it the same chapter id could mean seven different things.
      const res = await api.editPlan(plan.id, {
        ownerId: plan.ownerId, passId, subject, chapterId, date,
      });
      onPlanChange?.(res.plan);
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(null); }
  };

  /** id → its place in the syllabus, taken from the order they arrive in. */
  const chapterOrder = useMemo(
    () => new Map(chapters.map((ch, i) => [ch.id, i])),
    [chapters],
  );

  const columns = useMemo<ResponsiveColumn<Chapter>[]>(() => [
    {
      key: 'chapter',
      header: 'Chapter',
      mobile: 'title',
      sortable: true,
      /**
       * By POSITION IN THE SYLLABUS, not by parsing the number.
       *
       * The numbers are not numbers: "1.10" parses to 1.1 and collides with
       * "1.1", "2.3.1" parses to NaN and fell back to sorting by name, and a
       * syllabus that numbers by unit ("Unit II") has no number at all. The
       * chapters arrive in curriculum order, so their index IS the order the
       * book teaches them in — which is what someone sorting this column means.
       */
      sortValue: ch => chapterOrder.get(ch.id) ?? Number.MAX_SAFE_INTEGER,
      render: ch => (
        <span className="cs-name-plain">{ch.number ? `${ch.number} ` : ''}{ch.name}</span>
      ),
    },
    ...passes.map((p: StudyPlanPass): ResponsiveColumn<Chapter> => ({
      key: p.id,
      header: p.name,
      tdClassName: 'cs-plan-cell',
      sortable: true,
      // "What is due first for this pass" — undated chapters sort last either
      // way, which the table handles, so sorting surfaces the gaps too.
      sortValue: ch => targets(p.id).get(ch.id) ?? '',
      render: ch => (canEdit && editing ? (
        <DatePicker
          value={targets(p.id).get(ch.id) ?? ''}
          disabled={busy === `${p.id}:${ch.id}`}
          ariaLabel={`Date ${ch.name} is finished for ${p.name}`}
          placeholder="Set date"
          format="D MMM"
          onChange={iso => void write(`${p.id}:${ch.id}`, p.id, ch.id, iso || null)}
        />
      ) : (
        <span className={formatPlanDate(targets(p.id).get(ch.id)) ? '' : 'cs-muted'}>
          {formatPlanDate(targets(p.id).get(ch.id)) ?? NO_DATE}
        </span>
      )),
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [passes, plan, canEdit, editing, busy, subject, chapterOrder]);

  return (
    <div className="cs">
      <div className="cs-head">
        <h3>Chapter schedule</h3>
      </div>

      {/* One subject at a time — a selector rather than tabs, because a plan may
          cover ten and the grid below is the thing worth the width. Unlabelled:
          it sits under "Chapter schedule" with subject names in it, so a caption
          reading "Subject" only repeats what the options already say. */}
      {(plan.subjects ?? []).length > 1 && (
        <div className="cs-chapter-subject">
          <select
            value={subject}
            aria-label="Which subject's chapter dates"
            onChange={e => onSubjectChange?.(e.target.value)}
          >
            {(plan.subjects ?? []).map(sub => (
              <option key={sub.subject} value={sub.subject}>
                {subjectName?.(sub.subject) ?? sub.subject}
              </option>
            ))}
          </select>
        </div>
      )}

      {entry && canEdit && editing && chapters.length > 0 && (
        /* One button per pass, sitting over the column it fills. The pass date is
           a deadline; these dates are the route to it, and typing thirty by hand
           is why they are usually empty. */
        <div className="cs-fill-row" aria-hidden={false}>
          <span className="cs-fill-spacer" />
          {passes.map(p => {
            const deadline = plan.passDates?.[p.id];
            const filled = (entry.chapterTargets?.[p.id] ?? []).length > 0;
            return (
              <span key={p.id} className="cs-fill-cell">
                <button
                  type="button"
                  className="cs-btn cs-btn--sm"
                  disabled={!deadline || busy === `fill:${p.id}`}
                  title={deadline
                    ? `Space every chapter evenly between this Friday and ${deadline}`
                    // Disabled rather than hidden, and it says why: the deadline is
                    // what the dates are spaced towards, so there is nothing to
                    // generate against until it is set.
                    : `Set a date for ${p.name} first — that is what these are spaced towards`}
                  onClick={() => setGen({
                    pass: p,
                    start: defaultStartFor(p),
                    gapWeeks: DEFAULT_GAP_WEEKS,
                  })}
                >
                  {busy === `fill:${p.id}`
                    ? 'Generating…'
                    : filled ? 'Regenerate dates' : 'Generate dates'}
                </button>
              </span>
            );
          })}
        </div>
      )}

      {!entry ? (
        <p className="cs-muted">
          {(plan.subjects ?? []).length === 0
            ? 'Add a subject above, then its chapters can be dated here.'
            : `This plan does not cover ${subjectName?.(subject) ?? subject}.`}
        </p>
      ) : passes.length === 0 ? (
        <p className="cs-muted">
          This plan has no passes yet. Add one on the plan page — chapter dates hang off
          passes, so there is nothing to schedule against until then.
        </p>
      ) : chapters.length === 0 ? (
        <p className="cs-muted">
          No chapters resolved for this syllabus, so only the whole-plan pass dates can be
          set.
        </p>
      ) : (
        <ResponsiveTable
          rows={chapters}
          rowKey={ch => ch.id}
          columns={columns}
          className="cs-plan-table"
        />
      )}

      {notice && <p className="cs-warn">{notice}</p>}
      {error && <p className="cs-error">{error}</p>}

      <Modal
        isOpen={!!gen}
        onClose={() => setGen(null)}
        title={gen ? `Generate dates for ${gen.pass.name}` : ''}
        size="sm"
        footer={gen ? (
          <>
            <button type="button" className="cs-link" onClick={() => setGen(null)}>Cancel</button>
            <button
              type="button" className="cs-btn"
              disabled={busy === `fill:${gen.pass.id}`}
              onClick={() => {
                const g = gen;
                setGen(null);
                void fillChapterDates(g.pass, g.start, g.gapWeeks * 7);
              }}
            >Generate</button>
          </>
        ) : undefined}
      >
        {gen && (
          <div className="cs-gen-form">
            <label className="cs-field">
              <span>Start this pass on</span>
              <DatePicker
                value={gen.start}
                ariaLabel="Date this pass starts"
                onChange={iso => setGen({ ...gen, start: iso || gen.start })}
              />
              <span className="cs-meta">
                Chapters are spread from here to {gen.pass.name}&rsquo;s own date
                ({formatPlanDate(plan.passDates?.[gen.pass.id])}), each landing on a Friday.
              </span>
            </label>

            <label className="cs-field">
              <span>Least gap from the previous pass</span>
              <span className="cs-gap-input">
                <IntegerInput
                  min={0}
                  max={52}
                  value={gen.gapWeeks}
                  aria-label="Minimum weeks between passes for one chapter"
                  onChange={v => { if (v != null) setGen({ ...gen, gapWeeks: v }); }}
                />
                <span>weeks</span>
              </span>
              <span className="cs-meta">
                {/* Says what it protects against, since the number alone does not:
                    the constraint is per CHAPTER, not per pass. */}
                Measured per chapter, so no chapter is revised sooner than this after
                its own previous pass.
              </span>
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
}
